// Vault client-side encryption (TASK-VAULT-CLOUD-001, Model A).
//
// Files are encrypted with AES-256-GCM in the browser/WebView before they leave
// the device. The per-user key (DEK) is fetched once from the /api/vault-key
// broker (gated by the Supabase session) and cached in memory as a non-
// extractable CryptoKey. Ciphertext goes to the user's Google Drive; the key
// stays on our server (split custody — not zero-knowledge, so email reset works).

import { createClient } from '@/lib/supabase/client';
import { vaultApiPath } from '@/lib/vault/api';

let cachedKey: CryptoKey | null = null;

function b64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function bytesToB64(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

async function supabaseAccessToken(): Promise<string | null> {
  const supabase = createClient();
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

/** Fetch (and memoize) the per-user AES-GCM key from the broker. */
export async function getVaultKey(): Promise<CryptoKey> {
  if (cachedKey) return cachedKey;

  const token = await supabaseAccessToken();
  if (!token) throw new Error('Please sign in before using the Vault.');

  const res = await fetch(vaultApiPath('/api/vault-key'), {
    method: 'GET',
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.key) {
    throw new Error(data.error || 'Could not get the Vault encryption key.');
  }

  const raw = b64ToBytes(data.key);
  cachedKey = await crypto.subtle.importKey('raw', raw as BufferSource, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
  return cachedKey;
}

/** Encrypt a blob with the user's key; returns ciphertext + base64 IV. */
export async function encryptBlob(blob: Blob): Promise<{ cipher: Blob; iv: string }> {
  const key = await getVaultKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plain = new Uint8Array(await blob.arrayBuffer());
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    plain as BufferSource,
  );
  return { cipher: new Blob([encrypted]), iv: bytesToB64(iv) };
}

/** Decrypt ciphertext produced by encryptBlob. `type` sets the output Blob MIME. */
export async function decryptBlob(cipher: Blob, ivB64: string, type?: string | null): Promise<Blob> {
  const key = await getVaultKey();
  const iv = b64ToBytes(ivB64);
  const data = new Uint8Array(await cipher.arrayBuffer());
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    data as BufferSource,
  );
  return new Blob([decrypted], type ? { type } : undefined);
}

/** Drop the cached key (call on sign-out / Drive disconnect). */
export function clearVaultKeyCache(): void {
  cachedKey = null;
}
